import { ActionTree, MutationTree, GetterTree } from "vuex";
import { queryFromObject } from "@/utils/api-functions";
import Vue from "vue";

export interface IndexAppointmentPayload {
  activity_id?: number;
}

export interface CalendarAction {
  id: number;
  name: string;
  duration: number | null;
  dutchName: string;
}

export interface AppointmentTypeView {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  duration: number;
  color: string;
  type: string;
  group_id: number;
}

const initialState: AppointmentState = {
  all: [],
  actions: [],
  types: [],
  total: undefined,
  viewing: undefined,
};

const getters: GetterTree<AppointmentState, RootState> = {
  all(state: AppointmentState): Appointment[] {
    return state.all;
  },
  total(state: AppointmentState): number | undefined {
    return state.total;
  },
  actions(state: AppointmentState): CalendarAction[] {
    return state.actions;
  },
  types(state: AppointmentState): AppointmentTypeView[] {
    return state.types;
  },
  viewing(state: AppointmentState): Appointment | undefined {
    return state.viewing;
  },
};

const mutations: MutationTree<AppointmentState> = {
  RESET(state: AppointmentState) {
    state.all = [];
    state.viewing = undefined;
  },
  UPDATE_MODEL(state: AppointmentState, payload: Appointment) {
    const index = state.all.findIndex((model: Appointment) => model.id === payload.id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      Vue.set(state.all, index, payload);
    }

    if (state.viewing && state.viewing.id === payload.id) {
      state.viewing = payload;
    }
  },
  DELETE_MODEL(state: AppointmentState, id: number) {
    // @ts-ignore
    const index = state.all.findIndex((model: Appointment) => model.id == id);

    if (index >= 0) {
      state.all.splice(index, 1);
    }

    // @ts-ignore
    if (state.viewing && state.viewing.id === id) {
      state.viewing = undefined;
    }
  },
  ADD_MODEL(state: AppointmentState, payload: Appointment) {
    state.all.push(payload);
  },
  SET_MODELS(state: AppointmentState, payload: Appointment[]) {
    state.all = payload;
  },
  SET_ACTIONS(state: AppointmentState, payload: CalendarAction[]) {
    state.actions = payload;
  },
  SET_TYPES(state: AppointmentState, payload: AppointmentTypeView[]) {
    state.types = payload;
  },
  SET_TOTAL(state: AppointmentState, payload: number) {
    state.total = payload;
  },
  ADD_MODELS(state: AppointmentState, payload: Appointment[]) {
    state.all = [...state.all, ...payload];
  },
  SET_VIEWING(state: AppointmentState, payload: Appointment) {
    state.viewing = payload;
  },
};

const actions: ActionTree<AppointmentState, RootState> = {
  create({ commit, rootState }, payload: AppointmentCreatePayload) {
    return rootState.api
      .post(`appointments`, payload, { withCredentials: true })
      .then((response: { data: Appointment }) => {
        commit("ADD_MODEL", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  update({ commit, rootState }, { payload, id }: { payload: AppointmentCreatePayload; id: number }) {
    return rootState.api
      .put(`appointments/${id}`, payload, { withCredentials: true })
      .then((response: { data: Appointment }) => {
        commit("UPDATE_MODEL", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  index({ commit, rootState }, payload: IndexAppointmentPayload) {
    let headers = {};

    return rootState.api
      .get(`appointments?${payload ? queryFromObject(payload) : ""}`, { withCredentials: true, headers })
      .then((response: { data: { items: Activity[] }; total: number }) => {
        commit("SET_MODELS", response.data.items);
        commit("SET_TOTAL", response.total);

        return Promise.resolve(response.data.items);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  actions({ commit, rootState, state }) {
    if (state.actions && state.actions.length > 0) {
      return new Promise((resolve) => resolve(state.actions));
    }

    return rootState.api
      .get(`appointments/actions`, { withCredentials: true })
      .then((response: { data: CalendarAction[] }) => {
        commit("SET_ACTIONS", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  types({ commit, rootState, state, rootGetters }) {
    if (state.types && state.types.length > 0) {
      return new Promise((resolve) => resolve(state.types));
    }

    // Use rootGetters to check if user is a partner
    const isPartner = rootGetters["auth/me"]?.hasRole && rootGetters["auth/me"].hasRole("partner");
    const currentActivity = rootGetters["activity/viewing"];

    // Get current activity ID if in partner interface
    const activityId = isPartner && currentActivity?.id ? currentActivity.id : null;
    // Build query string if activityId exists
    const queryString = activityId ? `?activityId=${activityId}` : "";

    return rootState.api
      .get(`appointment-types${queryString}`, { withCredentials: true })
      .then((response: { data: AppointmentTypeView[] }) => {
        commit("SET_TYPES", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  read({ commit, rootState }, payload: { id: number }) {
    return rootState.api
      .get(`appointments/${payload.id}`, {
        withCredentials: true,
      })
      .then((response: { data: Appointment }) => {
        commit("UPDATE_MODEL", response.data);

        commit("SET_VIEWING", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  delete({ commit, rootState }, payload: number) {
    return rootState.api
      .delete(`appointments/${payload}`, { withCredentials: true })
      .then(() => {
        commit("DELETE_MODEL", payload);

        return Promise.resolve();
      })
      .catch((e: ErrorResponse) => {
        return Promise.reject(e);
      });
  },
  setViewing({ commit }, payload: Appointment) {
    commit("UPDATE_MODEL", payload);
    commit("SET_VIEWING", payload);
  },
  clear({ commit }) {
    commit("SET_MODELS", []);
    commit("SET_VIEWING", null);
  },
  clearViewing({ commit }) {
    commit("SET_VIEWING", null);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
